import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { EllipsisVerticalIcon, Trash2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import {
  type GetPropertySearchDrawerQuery,
  Property_Search_Source_Enum,
  type Property_Searches_Set_Input,
  type UserCard_UserFragment,
} from '@graphql/__generated__/graphql';
import { ContactQuickActions } from '@modules/_shared/components/ContactQuickActions';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  canDeletePropertySearchRow,
  isRefusedSaveError,
} from '@modules/_shared/components/property-search/propertySearchPermissions';
import { usePhoneCopyMenuItems } from '@modules/_shared/components/usePhoneCopyMenuItems';
import { UserCard } from '@modules/_shared/components/UserCard';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getDictionaryLabel } from '@modules/_shared/utils/dictionaries';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import {
  PropertySearchForm,
  type PropertySearchFormData,
} from '@modules/property-searches/components/PropertySearchForm';
import {
  DELETE_PROPERTY_SEARCHES,
  GET_PROPERTY_SEARCH_DRAWER,
  INSERT_PROPERTY_SEARCH,
  UPDATE_PROPERTY_SEARCHES,
} from '@modules/property-searches/propertySearchesQueries';
import { useLocale } from '@shared/hooks/locale';
import { getUUID, toGlobalId } from '@shared/utils/global-id';

type ContactCardProps = {
  user: UserCard_UserFragment | null;
  useLegacyLinks?: boolean;
};

const ContactCard: React.FC<ContactCardProps> = ({
  user,
  useLegacyLinks = false,
}) => {
  const { t } = useLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const phoneCopyMenuItems = usePhoneCopyMenuItems(user?.phone_numbers ?? []);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  if (user == null) {
    return null;
  }

  const open = Boolean(anchorEl);
  const userLink = useLegacyLinks
    ? `/v1/users/${toGlobalId('User', user.id)}`
    : `/users/${user.id}`;

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: '0 !important' }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {t('Contact')}
        </Typography>
        <Divider sx={{ mt: 1 }} />
        <UserCard
          user={user}
          userActions={
            <>
              <ContactQuickActions
                phoneNumbers={user.phone_numbers}
                emails={user.emails}
              />
              <IconButton edge="end" onClick={handleOpenMenu}>
                <EllipsisVerticalIcon size={20} />
              </IconButton>
            </>
          }
        />
        <Menu
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          anchorEl={anchorEl}
        >
          {phoneCopyMenuItems}
          <MenuItem component={Link} to={userLink}>
            {t('See profile')}
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

type PropertySearchDrawerProps = {
  onUpsert?: (id: string) => void;
  selectedContact?: string;
  propertySearchId?: string;
  onClose: () => void;
  /**
   * Called after a delete succeeds, so openers outside the Apollo cache (the
   * legacy Relay card) can refresh. Its absence also disables the action; the
   * permission itself is decided per row.
   */
  onDelete?: () => void;
  slotProps?: {
    drawer?: {
      sx?: SxProps<Theme>;
    };
  };
};

export const PropertySearchDrawer: React.FC<PropertySearchDrawerProps> = ({
  onUpsert,
  selectedContact,
  propertySearchId,
  onClose,
  slotProps,
  onDelete,
}) => {
  const { t } = useLocale();
  const { propertySearchId: paramsId } = useParams();
  const rawId = propertySearchId ?? paramsId ?? null;
  const id = rawId == null ? null : getUUID(rawId);
  const { breakpoints } = useTheme();
  const { hasuraRole, me } = useAppData();
  // Row-filter refusals of delete and save report here; other save errors stay
  // with RaForm.
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);

  const { data, error, loading } = useQuery(GET_PROPERTY_SEARCH_DRAWER, {
    variables: { id: id ?? '' },
    skip: id == null,
  });

  const loadedSearch = data?.property_searches_by_pk;
  const canDelete = useMemo(
    () =>
      loadedSearch != null &&
      me != null &&
      canDeletePropertySearchRow({
        hasuraRole,
        meId: me.id,
        myTeamIds: new Set(me.teams.map(({ team_id }) => team_id)),
        propertySearch: loadedSearch,
      }),
    [hasuraRole, me, loadedSearch],
  );

  const [deletePropertySearch, { loading: deleting }] = useMutation(
    DELETE_PROPERTY_SEARCHES,
    {
      onError: setMutationError,
    },
  );

  const dictionaries = useMemo(
    () =>
      data?.dictionaries.filter(
        (d): d is { id: string; name: string; label: string } =>
          d.label != null,
      ),
    [data?.dictionaries],
  );

  const getSourceLabel = useCallback(
    (source: string | null | undefined) =>
      source == null
        ? source
        : getDictionaryLabel(
            `property_search_source.${source}`,
            dictionaries,
            source,
          ),
    [dictionaries],
  );
  const getAggSourceLabel = useCallback(
    (source: string | null | undefined) =>
      source == null
        ? source
        : getDictionaryLabel(
            `property_search_aggregator_source.${source}`,
            dictionaries,
            source,
          ),
    [dictionaries],
  );

  const drawerActions = useMemo(
    () => [
      {
        label: t('Delete property search'),
        disabled: !canDelete || onDelete == null,
        onClick: () => {
          if (id == null || !canDelete) {
            return;
          }

          if (
            window.confirm(
              t('Are you sure you want to delete this property search?'),
            )
          ) {
            deletePropertySearch({
              variables: {
                ids: [id],
              },
              // Guarded: Apollo runs `update` on any non-error response, so an
              // unguarded evict would remove the row from every list on screen
              // while the snackbar says it is still there.
              update: (cache, { data }) => {
                if (data?.delete_property_searches?.affected_rows) {
                  cache.evict({ id: `property_searches:${id}` });
                }
              },
              // Zero rows means the row filter refused it (see
              // canDeletePropertySearchRow). Closing would read as "deleted".
              onCompleted: data => {
                if (!data.delete_property_searches?.affected_rows) {
                  setMutationError(
                    new Error(
                      t(
                        'This property search could not be deleted. It may belong to another team.',
                      ),
                    ),
                  );
                  return;
                }
                onDelete?.();
                onClose();
              },
            });
          }
        },
        icon: <Trash2Icon size={20} />,
      },
    ],
    [id, onClose, onDelete, t, deletePropertySearch, canDelete],
  );

  const isCreation = id == null;

  return (
    <Drawer
      onClose={onClose}
      maxWidth={breakpoints.values.md}
      sx={slotProps?.drawer?.sx}
      title={
        isCreation ? t('Create property search') : t('Edit property search')
      }
      actions={drawerActions}
      actionsLoading={deleting}
    >
      {loading ? (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : (
        <PropertySearchDrawerContent
          isCreation={isCreation}
          propertySearch={data?.property_searches_by_pk}
          getSourceLabel={getSourceLabel}
          getAggSourceLabel={getAggSourceLabel}
          onUpsert={onUpsert}
          selectedContact={selectedContact}
          onClose={onClose}
          onMutationError={setMutationError}
        />
      )}

      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </Drawer>
  );
};

type PropertySearchDrawerContentProps = {
  propertySearch: GetPropertySearchDrawerQuery['property_searches_by_pk'];
  isCreation: boolean;
  getSourceLabel: (
    source: string | null | undefined,
  ) => string | null | undefined;
  getAggSourceLabel: (
    source: string | null | undefined,
  ) => string | null | undefined;
  onClose: () => void;
  onMutationError: (error: ErrorLike) => void;
} & Omit<
  PropertySearchDrawerProps,
  'onClose' | 'propertySearchId' | 'slotProps' | 'onDelete'
>;

const PropertySearchDrawerContent: React.FC<
  PropertySearchDrawerContentProps
> = ({
  propertySearch,
  isCreation,
  getSourceLabel,
  getAggSourceLabel,
  onUpsert,
  selectedContact,
  onClose,
  onMutationError,
}) => {
  const { t, intlLocale } = useLocale();
  const [updatePropertySearch] = useMutation(UPDATE_PROPERTY_SEARCHES);
  const savingRefused = useCallback(
    () =>
      new Error(
        t(
          'This property search could not be saved. It may belong to another team.',
        ),
      ),
    [t],
  );
  const [insertPropertySearch] = useMutation(INSERT_PROPERTY_SEARCH);

  const onSubmit = useCallback(
    (data: PropertySearchFormData) => {
      if (!isCreation && propertySearch == null) {
        onClose();
        return Promise.resolve();
      }

      const {
        monthly_rent,
        gross_rent_room_monthly,
        gross_rent_m2_yearly,
        sale_price,
        sale_price_per_land_surface,
        sale_price_per_living_surface,
        built_surface,
        living_surface,
        land_surface,
        number_of_rooms,
        number_of_bathrooms,
        number_of_bedrooms,
        property_types,
        property_status,
        places,
        user,
        ...rest
      } = data;

      const input: Property_Searches_Set_Input = {
        ...rest,
        property_status: property_status === 'both' ? null : property_status,
        built_surface_gte: built_surface.gte,
        built_surface_lte: built_surface.lte,
        living_surface_gte: living_surface.gte,
        living_surface_lte: living_surface.lte,
        land_surface_gte: land_surface.gte,
        land_surface_lte: land_surface.lte,
        number_of_rooms_gte: number_of_rooms.gte,
        number_of_rooms_lte: number_of_rooms.lte,
        number_of_bathrooms_gte: number_of_bathrooms.gte,
        number_of_bathrooms_lte: number_of_bathrooms.lte,
        number_of_bedrooms_gte: number_of_bedrooms.gte,
        number_of_bedrooms_lte: number_of_bedrooms.lte,
      };

      if (data.offer_type === 'rent') {
        input.sale_price_gte = null;
        input.sale_price_lte = null;
        input.sale_price_per_land_surface_gte = null;
        input.sale_price_per_land_surface_lte = null;
        input.sale_price_per_living_surface_gte = null;
        input.sale_price_per_living_surface_lte = null;
        input.monthly_rent_gte = monthly_rent.gte;
        input.monthly_rent_lte = monthly_rent.lte;
        input.gross_rent_room_monthly_gte = gross_rent_room_monthly.gte;
        input.gross_rent_room_monthly_lte = gross_rent_room_monthly.lte;
        input.gross_rent_m2_yearly_gte = gross_rent_m2_yearly.gte;
        input.gross_rent_m2_yearly_lte = gross_rent_m2_yearly.lte;
      } else {
        input.sale_price_gte = sale_price.gte;
        input.sale_price_lte = sale_price.lte;
        input.sale_price_per_land_surface_gte = sale_price_per_land_surface.gte;
        input.sale_price_per_land_surface_lte = sale_price_per_land_surface.lte;
        input.sale_price_per_living_surface_gte =
          sale_price_per_living_surface.gte;
        input.sale_price_per_living_surface_lte =
          sale_price_per_living_surface.lte;
        input.monthly_rent_gte = null;
        input.monthly_rent_lte = null;
        input.gross_rent_room_monthly_gte = null;
        input.gross_rent_room_monthly_lte = null;
        input.gross_rent_m2_yearly_gte = null;
        input.gross_rent_m2_yearly_lte = null;
      }

      if (isCreation) {
        return insertPropertySearch({
          variables: {
            object: {
              ...input,
              source: Property_Search_Source_Enum.Crm,
              user_id: user,
              property_searches_places: {
                data: places.map(placeId => ({
                  place_id: placeId,
                })),
              },
              property_types: {
                data: property_types.map(type => ({
                  property_type_id: type.id,
                })),
              },
            },
          },
        }).then(result => {
          if (result.data?.insert_property_searches_one?.id) {
            onUpsert?.(result.data.insert_property_searches_one.id);
            onClose();
          }

          return result;
        });
      }

      // It cannot be null because of the previous if.
      const propertySearchId = propertySearch?.id as string;

      return updatePropertySearch({
        variables: {
          id: propertySearchId,
          propertySearch: input,
          propertyPlaces: places.map(placeId => ({
            place_id: placeId,
            property_search_id: propertySearchId,
          })),
          propertyTypes: property_types.map(type => ({
            property_type_id: type.id,
            property_search_id: propertySearchId,
          })),
        },
        update: (cache, { data }) => {
          // Same guard as the delete's evict: a null parent must never paint
          // places onto the card.
          if (data?.update_property_searches_by_pk == null) {
            return;
          }
          cache.modify({
            id: cache.identify({
              __typename: 'property_searches',
              id: propertySearchId,
            }),
            fields: {
              property_searches_places() {
                return data?.insert_property_searches_places?.returning;
              },
              property_types() {
                return data?.insert_property_searches_property_types?.returning;
              },
            },
          });
        },
      }).then(
        result => {
          // The row filter refuses an update as a null `_by_pk`, not as an
          // error, when the call carries no places or types to insert.
          if (result.data?.update_property_searches_by_pk?.id == null) {
            onMutationError(savingRefused());
            return result;
          }

          onUpsert?.(result.data.update_property_searches_by_pk.id);
          onClose();

          return result;
        },
        (error: unknown) => {
          // With places or types, the same refusal fails the call; see
          // isRefusedSaveError. Anything else goes to RaForm.
          if (
            CombinedGraphQLErrors.is(error) &&
            isRefusedSaveError(error.errors)
          ) {
            onMutationError(savingRefused());
            return undefined;
          }
          throw error;
        },
      );
    },
    [
      propertySearch,
      updatePropertySearch,
      isCreation,
      insertPropertySearch,
      onUpsert,
      onClose,
      onMutationError,
      savingRefused,
    ],
  );

  return isCreation ? (
    <PropertySearchForm
      onSubmit={onSubmit}
      includeContact
      preselectedContact={selectedContact}
      onClose={onClose}
    />
  ) : (
    <>
      <Box
        sx={{
          p: 2,
          pb: 0,
        }}
      >
        <ContactCard user={propertySearch?.user ?? null} />
      </Box>
      <PropertySearchForm
        propertySearch={propertySearch}
        onSubmit={onSubmit}
        onClose={onClose}
      >
        <Box sx={{ px: 2, mb: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('Metadata')}
              </Typography>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1">{t('Created at')}</Typography>
                <Typography variant="body2">
                  {toHumanReadableDate(intlLocale, propertySearch?.created_at)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1">{t('Source')}</Typography>
                <Typography variant="body2">
                  {propertySearch?.source === 'aggregator'
                    ? `${t('Aggregator')} - ${getAggSourceLabel(
                        propertySearch?.aggregator_source,
                      )}`
                    : getSourceLabel(propertySearch?.source)}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </PropertySearchForm>
    </>
  );
};
